"use client";

import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";

type ProFeature = "tests" | "audio" | "templates";

export function ProFeatureLockOverlay({
  title,
  description,
  children,
  className,
  feature,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  feature?: ProFeature;
}) {
  const titleId = useId();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!feature || trackedRef.current) {
      return;
    }
    trackedRef.current = true;
    track(ANALYTICS_EVENTS.proLockOverlayShown, { feature });
    if (feature === "tests") {
      track(ANALYTICS_EVENTS.testProGateBlocked);
    } else if (feature === "audio") {
      track(ANALYTICS_EVENTS.audioProGateBlocked);
    } else {
      track(ANALYTICS_EVENTS.templateProGateBlocked);
    }
  }, [feature]);

  return (
    <div className={cn("relative isolate w-full overflow-hidden", className)}>
      {/* Blurred background content */}
      <div
        className="pointer-events-none select-none [&_a]:pointer-events-none [&_button]:pointer-events-none [&_input]:pointer-events-none [&_label]:pointer-events-none [&_select]:pointer-events-none"
        aria-hidden="true"
      >
        {children}
      </div>

      {/* Lock overlay */}
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-[inherit] px-6 py-12 text-center"
        style={{
          background: "color-mix(in oklab, var(--background) 80%, transparent)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          boxShadow: "inset 0 0 0 1px color-mix(in oklab, var(--border) 50%, transparent)",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Lock icon */}
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-accent/30 bg-accent/15 shadow-sm">
          <Lock className="h-6 w-6 text-accent-foreground" />
        </div>

        <div className="flex flex-col gap-2">
          <p
            id={titleId}
            className="max-w-sm text-base font-semibold leading-snug text-foreground"
          >
            {title}
          </p>
          {description && (
            <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </div>

        <Link
          href={`/buy?section=plans&focus=pro&entry=${feature ?? "locked_feature"}`}
          className="pointer-events-auto inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-accent-foreground transition-[opacity,transform] hover:opacity-90 active:scale-[0.98]"
        >
          View Pro in Plan &amp; billing
        </Link>
      </div>
    </div>
  );
}
